import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { useDeckOverview } from '../../hooks/useDecks.js';
import { useSettings } from '../../app/settingsStore.js';
import { useNow } from '../../hooks/useNow.js';
import PageHeader from '../../components/PageHeader.jsx';
import Button from '../../components/Button.jsx';
import Icon from '../../components/Icon.jsx';
import SyncIndicator from '../../components/SyncIndicator.jsx';
import HelpButton from '../../components/HelpButton.jsx';
import { ActionTile, TileGrid } from '../../components/ActionList.jsx';
import DeckTree from './DeckTree.jsx';
import DeckFormSheet from './DeckFormSheet.jsx';
import { TodayCard } from './TodayCard.jsx';
import AccountPitch from '../auth/AccountPitch.jsx';
import styles from './Decks.module.scss';

const STEPS = [
  { icon: 'decks', title: 'Make a deck', text: 'A deck is one system or course, like “Renal” or “Pharm”.' },
  { icon: 'plus', title: 'Add cards', text: 'Write a question on the front and the answer on the back.' },
  { icon: 'play', title: 'Study a little each day', text: 'Recall shows hard cards more often and easy ones less.' },
];

export default function HomePage() {
  const overview = useDeckOverview();
  const showToday = useSettings((s) => s.homeToday);
  const now = useNow(60000);
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(params.get('newDeck') === '1');

  const closeCreate = () => {
    setCreating(false);
    if (params.get('newDeck')) setParams({}, { replace: true });
  };

  const totals = { new: 0, learn: 0, review: 0 };
  if (overview) {
    for (const root of overview.tree) {
      const c = overview.counts.get(root.id);
      if (c) {
        totals.new += c.new;
        totals.learn += c.learn;
        totals.review += c.review;
      }
    }
  }
  const empty = overview && overview.tree.length === 0;
  const date = new Date(now).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <div className={styles.page}>
      <PageHeader
        title={empty ? 'Let’s get started' : 'Your decks'}
        subtitle={date}
        actions={
          <>
            <span className={styles.phoneOnly}>
              <SyncIndicator compact />
            </span>
            <HelpButton topic={empty ? 'start' : 'decks'} />
          </>
        }
      />
      <div className={styles.body}>
        {overview === null ? null : empty ? (
          <div className={styles.welcome}>
            <ol className={styles.steps}>
              {STEPS.map((s, i) => (
                <li key={s.title} className={styles.step}>
                  <span className={styles.stepNum}>{i + 1}</span>
                  <span className={styles.stepText}>
                    <strong>{s.title}</strong>
                    <span>{s.text}</span>
                  </span>
                  <Icon name={s.icon} size={22} className={styles.stepIcon} />
                </li>
              ))}
            </ol>
            <div className={styles.welcomeActions}>
              <Button variant="primary" size="lg" block icon="plus" onClick={() => setCreating(true)}>
                Create your first deck
              </Button>
              <Button size="lg" block icon="play" to="/welcome">
                Take the quick tour
              </Button>
            </div>
            <p className={styles.reassure}>
              <Icon name="shield" size={16} /> Everything saves on this device right away. An account is optional.
            </p>
          </div>
        ) : (
          <>
            <AccountPitch variant="banner" />
            <div className={styles.split}>
            <div className={styles.side}>
              {showToday ? <TodayCard counts={totals} to="/study?go=today&from=home" title="Today" /> : null}
              <div className={styles.desktopOnly}>
                <TileGrid columns={2}>
                  <ActionTile icon="plus" label="Add a card" to="/add" />
                  <ActionTile icon="browse" label="Find a card" to="/browse" />
                </TileGrid>
              </div>
            </div>
            <section className={styles.section} aria-labelledby="decks-title">
              <div className={styles.sectionHead}>
                <h2 className={styles.sectionTitle} id="decks-title">
                  Decks
                </h2>
                <Button size="sm" icon="plus" onClick={() => setCreating(true)}>
                  New deck
                </Button>
              </div>
              <DeckTree tree={overview.tree} counts={overview.counts} />
            </section>
            </div>
          </>
        )}
      </div>
      <DeckFormSheet open={creating} onClose={closeCreate} mode="create" />
    </div>
  );
}
